import apiRequest from '../api/request';

/**
 * The seam between the API and the UI.
 *
 * The backend speaks snake_case and mirrors the column names; the components
 * speak camelCase. Mapping here rather than renaming fields across every
 * component means one place to change when a field moves, and the components
 * stay readable.
 *
 * The four scopes and four types are fixed enum values rather than data, so
 * they live here as constants. Assignment options are NOT — they depend on who
 * is currently in each role, so they come from the server.
 */

const BASE = 'incentive-challenges';

export const CHALLENGE_TYPES = [
  { id: 'admission', value: 'Admission' },
  { id: 'sales_revenue', value: 'Sales revenue' },
  { id: 'fee_collection', value: 'Fee collection' },
  { id: 'fee_plus_sales_revenue', value: 'Fee + Sales revenue' },
];

export const CHALLENGE_STATUSES = [
  { id: 'upcoming', value: 'Upcoming' },
  { id: 'active', value: 'Active' },
  { id: 'completed', value: 'Completed' },
];

/**
 * How a challenge's duration is cut into rounds. Each round is judged on its
 * own against the same ladder, and the prizes from every round add up. 'none'
 * is the whole duration as one round — every challenge before rounds existed.
 */
export const PARTITION_TYPES = [
  { id: 'none', value: 'Whole duration' },
  { id: 'daily', value: 'Daily' },
  { id: 'weekly', value: 'Weekly' },
  { id: 'every_n', value: 'Every N days' },
];

/** The longest every-N round the API accepts. */
export const MAX_PARTITION_DAYS = 31;

/** Days per round, or null for the whole duration. */
export function roundLength(partitionType, partitionDays) {
  if (partitionType === 'daily') return 1;
  if (partitionType === 'weekly') return 7;
  if (partitionType === 'every_n') return Number(partitionDays) || null;
  return null;
}

/** Inclusive day count between two yyyy-mm-dd dates. */
export function daysBetween(startDate, endDate) {
  const ms = new Date(`${endDate}T00:00:00Z`) - new Date(`${startDate}T00:00:00Z`);
  return Math.round(ms / 86400000) + 1;
}

/** "Daily × 10", "Weekly × 2", "Every 3 days × 4" — or null when not partitioned. */
export function partitionLabel(partitionType, partitionDays, totalRounds) {
  if (!partitionType || partitionType === 'none') return null;
  const name =
    partitionType === 'every_n'
      ? `Every ${partitionDays} days`
      : PARTITION_TYPES.find((p) => p.id === partitionType)?.value || partitionType;
  return totalRounds ? `${name} × ${totalRounds}` : name;
}

/** The four tutoring projects plus OGD, as department ids. */
export const CHALLENGE_SCOPES = [
  { id: 20, value: 'Classroom' },
  { id: 3, value: 'Foundation' },
  { id: 21, value: 'Little Genie' },
  { id: 22, value: 'Speak Pro' },
  { id: 11, value: 'OGD' },
];

export const scopeLabel = (deptId) =>
  CHALLENGE_SCOPES.find((s) => s.id === Number(deptId))?.value || '—';

/** API row -> the shape the table and dialogs already read. */
export function toChallenge(row) {
  return {
    id: row.incentive_challenge_id,
    name: row.challenge_name,
    type: row.challenge_type,
    scopeDeptId: row.scope_dept_id,
    scopeLabel: scopeLabel(row.scope_dept_id),
    assignmentType: row.assignment_type,
    assignmentRef: row.assignment_ref,
    assignmentLabel: row.assignment_label,
    startDate: String(row.start_date).slice(0, 10),
    endDate: String(row.end_date).slice(0, 10),
    status: row.status,
    locked: row.locked,
    participantUserIds: row.participant_user_ids || [],
    // Kept in the API's own shape as well as the form's, because the list
    // column reads the rungs straight off the row.
    bands: (row.bands || []).map((b) => ({
      from: b.revenue_target_from,
      to: b.revenue_target_to,
      tiers: (b.tiers || []).map((t) => ({ target: Number(t.target), prize: t.prize })),
    })),
    isGroup: row.is_group,
    partitionType: row.partition_type || 'none',
    partitionDays: row.partition_days ?? null,
    // Counts only, on the list endpoint: rounds so far, and the wins in them.
    partition: row.partition ?? null,

    // Present on the list endpoint, absent on a single fetch.
    participants: row.participants ?? 0,
    achievers: row.achievers ?? null,
    achieved: Number(row.achieved ?? 0),
    percent: row.percent ?? null,
    teamAchieved: row.team_achieved ?? null,
  };
}

/** Form values -> the API's field names. */
export function toPayload(values) {
  return {
    challenge_name: values.name,
    challenge_type: values.type,
    scope_dept_id: Number(values.scopeDeptId),
    assignment_type: values.assignmentType,
    assignment_ref: String(values.assignmentRef),
    // Only sent when the form has a better name than the role would give — a
    // per-person team challenge is saved as "CRO" and would otherwise lose
    // which team it was. The API falls back to the option's own label.
    ...(values.assignmentLabel ? { assignment_label: values.assignmentLabel } : {}),
    start_date: values.startDate,
    end_date: values.endDate,
    partition_type: values.partitionType || 'none',
    ...(values.partitionType === 'every_n' ? { partition_days: Number(values.partitionDays) } : {}),
    // Empty means everyone — the API clears the rows rather than emptying the
    // challenge, so leaving the picker on "All" keeps later joiners included.
    participant_user_ids: values.participantUserIds || [],
    // The challenge IS its ladder — one row or several — so these always go.
    bands: (values.bands || []).map((b) => ({
      revenue_target_from: b.from === '' || b.from === null ? null : Number(b.from),
      revenue_target_to: b.to === '' || b.to === null ? null : Number(b.to),
      tiers: (b.tiers || []).map((t) => ({ target: Number(t.target), prize: t.prize })),
    })),
  };
}

/**
 * How a target group reads as a column heading.
 *
 * Built from the two figures rather than stored beside them — a saved label
 * only gets a chance to drift away from the numbers it describes, which is why
 * the band table has no label column.
 *
 * Accepts either shape: `{ from, to }` as the form holds it, or
 * `{ revenue_target_from, revenue_target_to }` as the API returns it.
 */
export function bandHeading(band) {
  const money = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  const from = band.from ?? band.revenue_target_from ?? null;
  const to = band.to ?? band.revenue_target_to ?? null;
  if ((from ?? '') === '' && (to ?? '') === '') return 'Everyone';
  if ((from ?? '') === '') return `Up to ${money(to)}`;
  if ((to ?? '') === '') return `${money(from)} and above`;
  return `${money(from)} – ${money(to)}`;
}

/**
 * Participant row -> what ParticipantsDialog reads.
 *
 * A pooled target gives a member a share of the group's running total; there is
 * no personal number for them to miss, so no gap and no pass/fail. On a role
 * challenge everyone carries the same target, so they get both.
 */
export function toParticipant(row, { isGroup, target, total }) {
  const achieved = Number(row.achieved);
  const base = {
    userId: row.user_id,
    name: row.user_name,
    role: row.role_label,
    achieved,
  };

  if (isGroup) {
    return { ...base, share: total > 0 ? Math.round((achieved / total) * 100) : 0 };
  }

  return {
    ...base,
    target,
    gap: Math.max(0, target - achieved),
    percent: target > 0 ? Math.round((achieved / target) * 100) : 0,
    status: row.is_achiever ? 'achieved' : 'in_progress',
    // Head roles (PM / CRM / SOGO / OGM) record no payments themselves — their
    // figure is the output of the people under them, listed here so it is never
    // an unexplained lump sum.
    members: (row.members || []).map((m) => ({
      userId: m.user_id,
      name: m.user_name,
      role: m.role_label,
      achieved: Number(m.achieved),
      isHead: Boolean(m.is_head),
    })),
    // Partitioned challenges only: every round, for the people × days grid.
    partition: row.partition ?? null,
  };
}

export const listChallenges = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  );
  return apiRequest({ method: 'get', url: `${BASE}?${query}` });
};

export const createChallenge = (values) =>
  apiRequest({ method: 'post', url: BASE, data: toPayload(values) });

export const updateChallenge = (id, values) =>
  apiRequest({ method: 'put', url: `${BASE}/${id}`, data: toPayload(values) });

export const deleteChallenge = (id) => apiRequest({ method: 'delete', url: `${BASE}/${id}` });

/** One challenge in full. The list omits the hand-picked people; this carries them. */
export const getChallengeById = (id) => apiRequest({ method: 'get', url: `${BASE}/${id}` });

export const getAssignmentOptions = (scope) =>
  apiRequest({ method: 'get', url: `${BASE}/options?scope=${scope}` });

/** The names behind an assignment, for the people picker on a head role. */
export const getAssignmentPeople = ({ scope, type, ref }) =>
  apiRequest({
    method: 'get',
    url: `${BASE}/assignment-people?scope=${scope}&type=${type}&ref=${encodeURIComponent(ref)}`,
  });

export const getChallengeAnalytics = (id) =>
  apiRequest({ method: 'get', url: `${BASE}/${id}/analytics` });

export const getMyChallenges = () => apiRequest({ method: 'get', url: `${BASE}/me` });
